import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import uniqid from 'uniqid';
import Case from 'case';
import { Box } from 'grommet';
import { CurrentDateContext, useAppTheme } from '@Components/Context';
import {
  Seo,
  AppButton,
  Spinning,
  SubmissionLoader,
  SimpleModal,
} from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import {
  ProductFormFieldContainer,
  ProductFormDropdownSelect,
  ProductFormSectionWrapper,
  ProductFormSingleTextInput,
  ProductFormHybridInputSelect,
  ProductFormCheckBoxInput,
} from '@Components/Partial/Product/NewSearch';
import {
  fetchCompanyListRequest,
  clearCompanyListRequest,
  clearFileImportResultRequest,
  fetchAdminSearchTypesRequest,
  clearAdminSearchTypesRequest,
  submitCreateBulkSearchesImportRequest,
  fetchCompanyFormFieldsRequest,
  clearCompanyFormFieldsRequest,
} from '@Actions';
import ImportResultSummary from './ImportResultSummary';
import DynamicFormRenderer from '../../Product/Searches/NewSearch/DynamicFormRenderer';

const FormContents = ({
  small,
  loading,
  config,
  searchTypeDisplay,
  requiredFormInputs,
  optionalFormInputs,
  formValues,
  handleSearchType,
  handleFormValues,
  setFormValues,
  minStartDate,
  timeZone,
  setTimeZone,
  searchTypeKey,
  resetForm,
  submitEnabled,
  handleFormSubmit,
  uploadMethod = 'csv',
  onUseCatalogChange = null,
  companyList = null,
}) => {
  const { theme: { page, button, navigation } } = useAppTheme();
  if (loading) {
    return (
      <Box flex align="center" justify="center" background="white">
        <Spinning size="large" color={page.highlight} />
      </Box>
    );
  }

  const formattedCompanies = companyList?.map(({ id, name }) => ({ api: id, display: name })) || [];

  return (
    <Box
      flex
      direction={small ? 'column' : 'row'}
      gap="2rem"
      justify={small ? 'start' : 'evenly'}
      pad={small ? '1.5rem' : { horizontal: '1.5rem', vertical: '2rem' }}
      width="100%"
    >
      <ProductFormSectionWrapper
        small={small}
        sectionBg={page.altBg}
        sectionTitle="Required Fields"
        titleColor={page.primaryText}
        borderColor={navigation.border}
        boxStyle={!small ? { flex: 1, minWidth: 0 } : {}}
      >
        {/* --- Required search type selection --- */}
        {Array.isArray(config?.searchTypes) && config.searchTypes.length > 1 && (
          <ProductFormFieldContainer
            small={small}
            fieldTitle="Search Type"
            primaryText={page.primaryText}
            hintText={page.hintText}
            borderColor={navigation.border}
          >
            <ProductFormDropdownSelect
              selectable
              autoSelect
              label={searchTypeDisplay}
              handleFormValues={(searchTypeName) => handleSearchType(searchTypeName)}
              options={config && config.searchTypes}
            />
          </ProductFormFieldContainer>
        )}
        {/* --- Inputs for company_id and file_name --- */}
        <ProductFormFieldContainer
          small={small}
          fieldTitle="Import Name"
          primaryText={page.primaryText}
          hintText={page.hintText}
          borderColor={navigation.border}
        >
          <ProductFormSingleTextInput
            key={formValues.resetKey}
            focusHighlight={page.focusHighlight}
            placeholder="Enter a unique name for this import"
            small={small}
            value={formValues.fileName || ''}
            handleFormValues={(val) => setFormValues({ ...formValues, fileName: val })}
          />
        </ProductFormFieldContainer>
        <ProductFormFieldContainer
          small={small}
          fieldTitle="Company ID"
          description="Select a company to populate company-specific fields (if applicable)"
          primaryText={page.primaryText}
          hintText={page.hintText}
          borderColor={navigation.border}
        >
          <ProductFormHybridInputSelect
            searchable
            key={formValues.resetKey}
            inputProps={{
              placeholder: 'Enter an ID or select from dropdown',
            }}
            dropdownProps={{
              inputConfig: {
                overrideOptions: formattedCompanies,
              },
            }}
            small={small}
            value={formValues.companyId || ''}
            handleFormValues={(val) => setFormValues({ ...formValues, companyId: val })}
          />
        </ProductFormFieldContainer>
        {/* --- Dynamic fields from config --- */}
        {Array.isArray(requiredFormInputs)
          && requiredFormInputs.map((dt) => (
            <DynamicFormRenderer
              isBulkSearch
              key={dt.key}
              resetKey={formValues.resetKey}
              searchTypeKey={searchTypeKey}
              fieldData={dt}
              small={small}
              handleFormValues={(value, key) => handleFormValues(value, key)}
              formValues={formValues.searchFieldValues}
              minStartDate={minStartDate}
              timeZone={timeZone}
              setTimeZone={setTimeZone}
              useCatalog={uploadMethod === 'catalog'}
              onUseCatalogChange={onUseCatalogChange}
            />
          ))}
      </ProductFormSectionWrapper>
      <Box gap="2rem" direction="column" style={!small ? { flex: 1, minWidth: 0 } : {}}>
        <ProductFormSectionWrapper
          small={small}
          sectionTitle="Optional Fields"
          sectionBg={page.altBg}
          titleColor={page.primaryText}
          borderColor={navigation.border}
        >
          <ProductFormFieldContainer
            small={small}
            fieldTitle="Result Release Frequency"
            description="If selected, search results will be released based on the Search Run Frequency set in the company's Search feature"
            primaryText={page.primaryText}
            hintText={page.hintText}
            borderColor={navigation.border}
          >
            <ProductFormCheckBoxInput
              small={small}
              key={formValues.resetKey}
              bgColor="white"
              borderColor={navigation.border}
              checkmarkColor={page.highlight}
              focusHighlight={page.focusHighlight}
              value={formValues.searchFieldValues.use_result_release_frequency || false}
              handleFormValues={() => handleFormValues(
                !formValues.searchFieldValues.use_result_release_frequency,
                'use_result_release_frequency',
              )}
              label="Use result release frequency"
            />
          </ProductFormFieldContainer>
          {Array.isArray(optionalFormInputs) && optionalFormInputs?.length > 0 && (
            optionalFormInputs.map((dt) => (
              <DynamicFormRenderer
                key={dt.key}
                resetKey={formValues.resetKey}
                searchTypeKey={searchTypeKey}
                fieldData={dt}
                small={small}
                handleFormValues={(value, key) => handleFormValues(value, key)}
                formValues={formValues.searchFieldValues}
                minStartDate={minStartDate}
                timeZone={timeZone}
                setTimeZone={setTimeZone}
              />
            )))}
          <ProductFormFieldContainer
            small={small}
            fieldTitle="Track False Positive Results"
            description="If selected, a sample of false positive results will be tracked for this batch of searches"
            primaryText={page.primaryText}
            hintText={page.hintText}
            borderColor={navigation.border}
          >
            <ProductFormCheckBoxInput
              small={small}
              key={formValues.resetKey}
              bgColor="white"
              borderColor={navigation.border}
              checkmarkColor={page.highlight}
              focusHighlight={page.focusHighlight}
              value={formValues.searchFieldValues.track_false_positive_results || false}
              handleFormValues={() => handleFormValues(
                !formValues.searchFieldValues.track_false_positive_results,
                'track_false_positive_results',
              )}
              label="Track false positive results"
            />
          </ProductFormFieldContainer>
        </ProductFormSectionWrapper>
        <Box direction="row" gap="1rem" justify="center">
          <AppButton
            width="6.5rem"
            onClick={() => resetForm()}
            level="dynamicLarge"
            color={button.primaryBg}
            fontWeight={600}
            label="Cancel"
          />
          <AppButton
            overrideHover
            disabled={!submitEnabled}
            onClick={(e) => (submitEnabled ? handleFormSubmit(e) : null)}
            level="dynamicLarge"
            color={button.primaryText}
            bgColor={button.primaryBg}
            fontWeight={600}
            label="Create Searches"
          />
        </Box>
      </Box>
    </Box>
  );
};

FormContents.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  config: PropTypes.shape({
    searchTypes: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        name: PropTypes.string.isRequired,
        id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        fieldData: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string.isRequired,
            order: PropTypes.number.isRequired,
            config: PropTypes.objectOf(PropTypes.any),
            required: PropTypes.bool,
            fieldName: PropTypes.string.isRequired,
            fieldType: PropTypes.string.isRequired,
            selectable: PropTypes.bool,
            description: PropTypes.string,
          }).isRequired,
        ).isRequired,
      }),
    ),
  }),
  searchTypeDisplay: PropTypes.string,
  requiredFormInputs: PropTypes.arrayOf(PropTypes.any),
  optionalFormInputs: PropTypes.arrayOf(PropTypes.any),
  formValues: PropTypes.shape({
    searchType: PropTypes.string,
    companyId: PropTypes.string,
    fileName: PropTypes.string,
    searchFieldValues: PropTypes.objectOf(PropTypes.any),
    resetKey: PropTypes.string,
  }),
  companyList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  handleSearchType: PropTypes.func.isRequired,
  handleFormValues: PropTypes.func.isRequired,
  setFormValues: PropTypes.func.isRequired,
  minStartDate: PropTypes.instanceOf(Date),
  timeZone: PropTypes.string.isRequired,
  setTimeZone: PropTypes.func.isRequired,
  searchTypeKey: PropTypes.string,
  resetForm: PropTypes.func.isRequired,
  submitEnabled: PropTypes.bool,
  handleFormSubmit: PropTypes.func.isRequired,
  uploadMethod: PropTypes.string,
  onUseCatalogChange: PropTypes.func,
};

// Field types that represent file/media uploads
const MEDIA_FIELD_TYPES = ['SINGLE_MEDIA_UPLOAD', 'MULTIPLE_MEDIA_UPLOAD', 'file'];

const ImportSearchesPage = ({
  small,
  location,
  loading,
  submitLoading,
  fetchFormConfig,
  clearFormConfig,
  fetchCompanyList,
  clearCompanyList,
  fetchCompanyFormFields,
  clearCompanyFormFields,
  submitImportRequest,
  clearFileImportResult,
  companyList = null,
  companyFormFields = null,
  importResult = null,
  formConfig = null,
}) => {
  const [searchType, setSearchType] = React.useState(null);
  const [useConfig, setUseConfig] = React.useState(null);
  const [searchTypeKey, setSearchTypeKey] = React.useState(null);
  const [formConfigResult, setFormConfigResult] = React.useState(null);
  const [requiredFormInputs, setRequiredFormInputs] = React.useState(null);
  const [optionalFormInputs, setOptionalFormInputs] = React.useState(null);
  const [configLoading, setConfigLoading] = React.useState(true);
  const [formValues, setFormValues] = React.useState({
    fileName: '',
    companyId: '',
    searchType: null,
    searchFieldValues: {},
    resetKey: uniqid(),
  });
  const [selectedTimeZone, setSelectedTimeZone] = React.useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [uploadMethod, setUploadMethod] = React.useState('csv');
  const { today } = React.useContext(CurrentDateContext);
  const { theme: { page } } = useAppTheme();

  React.useEffect(() => {
    fetchFormConfig();
    if (!companyList) fetchCompanyList();

    return () => {
      clearFormConfig();
      clearCompanyList();
    };
  }, []);

  React.useEffect(() => {
    if (formConfig) {
      setUseConfig(formConfig);
      setSearchType(formConfig.searchTypes[0].name);
    }
  }, [formConfig]);

  React.useEffect(() => {
    if (useConfig && searchType) {
      setFormConfigResult(_.find(useConfig.searchTypes, { name: searchType }).fieldData);
      setFormValues({ searchType, searchFieldValues: {}, resetKey: uniqid() });
      setSearchTypeKey(Case.camel(_.find(useConfig.searchTypes, { name: searchType })?.category));
    }
  }, [searchType, useConfig]);

  const searchTypeDisplay = useConfig
    && (searchType
      ? _.find(useConfig.searchTypes, { name: searchType }).name
      : useConfig.searchTypes[0].name);
  const searchTypeId = useConfig
    && (searchType
      ? _.find(useConfig.searchTypes, { name: searchType }).id
      : useConfig.searchTypes[0].id);

  React.useEffect(() => {
    const debouncedFetch = _.debounce(() => {
      fetchCompanyFormFields({ searchTypeId, companyId: formValues.companyId });
    }, 350);

    if (formValues.companyId && searchTypeId) {
      debouncedFetch();
    }

    return () => {
      clearCompanyFormFields();
      debouncedFetch.cancel();
    };
  }, [formValues.companyId, searchTypeId]);

  React.useEffect(() => {
    if (formConfigResult) {
      let mergedInputs = Array.isArray(formConfigResult) ? [...formConfigResult] : [];
      if (companyFormFields && Array.isArray(companyFormFields?.fields)
        && companyFormFields.fields.length > 0
      ) {
        mergedInputs = mergedInputs.concat(companyFormFields.fields);
      }
      const partitionedResults = _.partition(mergedInputs, 'required');
      const [requiredInputs, optionalInputs] = partitionedResults;

      const filteredRequiredInputs = _.filter(
        requiredInputs,
        ({ config }) => !config?.supportedCategoryPlatforms && !config?.supportedRecurrence,
      );
      const filteredOptionalInputs = _.filter(
        optionalInputs,
        ({ config }) => !config?.supportedCategoryPlatforms && !config?.supportedRecurrence,
      );

      setRequiredFormInputs(filteredRequiredInputs);
      setOptionalFormInputs(filteredOptionalInputs);
      setConfigLoading(false);
    }
  }, [formConfigResult, companyFormFields]);

  const checkInputRequirements = (config) => {
    const selectedPlatforms = formValues.searchFieldValues.platform;
    const selectedRecurrence = formValues.searchFieldValues.recurrence;
    const currentPlatformConfig = config?.supportedCategoryPlatforms
      && config.supportedCategoryPlatforms[searchTypeKey];

    if (currentPlatformConfig && config?.supportedRecurrence) {
      return (
        selectedPlatforms?.some((p) => currentPlatformConfig.includes(p))
        && config.supportedRecurrence === selectedRecurrence
      );
    }

    if (currentPlatformConfig && selectedPlatforms?.length > 0) {
      return selectedPlatforms?.some((p) => currentPlatformConfig.includes(p));
    }

    if (config?.supportedRecurrence) {
      return config.supportedRecurrence === selectedRecurrence;
    }

    return true;
  };

  React.useEffect(() => {
    if (!formConfigResult) return;

    // Partition and filter inputs based on requirements
    let mergedInputs = Array.isArray(formConfigResult) ? [...formConfigResult] : [];
    if (companyFormFields && Array.isArray(companyFormFields?.fields)) {
      mergedInputs = mergedInputs.concat(companyFormFields.fields);
    }
    const partitionedResults = _.partition(mergedInputs, 'required');
    const [requiredInputs, optionalInputs] = partitionedResults;
    const filteredRequiredInputs = _.filter(
      requiredInputs,
      ({ config }) => checkInputRequirements(config),
    );
    const filteredOptionalInputs = _.filter(
      optionalInputs,
      ({ config }) => checkInputRequirements(config),
    );

    setRequiredFormInputs(filteredRequiredInputs);
    setOptionalFormInputs(filteredOptionalInputs);

    // Remove unsupported keys from formValues, but preserve valid ones
    const requiredKeys = filteredRequiredInputs.map(({ key }) => key);
    const optionalKeys = filteredOptionalInputs.map(({ key }) => key);
    const allowedKeys = [...requiredKeys, ...optionalKeys];
    const selectedKeys = Object.keys(formValues.searchFieldValues);
    // Preserve keys that are not in backend config but needed for API
    const PRESERVED_KEYS = [
      'use_catalog',
      'use_result_release_frequency',
      'track_false_positive_results',
    ];
    const unsupportedKeys = selectedKeys.filter(
      (k) => !allowedKeys.includes(k) && !PRESERVED_KEYS.includes(k),
    );

    if (unsupportedKeys.length > 0) {
      setFormValues((prev) => {
        const updatedValues = { ...prev.searchFieldValues };
        unsupportedKeys.forEach((key) => {
          delete updatedValues[key];
        });

        if (Object.keys(updatedValues).length !== selectedKeys.length) {
          return { ...prev, searchFieldValues: updatedValues };
        }
        return prev;
      });
    }
  }, [formConfigResult, formValues.searchFieldValues]);

  const handleFormValues = (value, key) => {
    const updatedValues = { ...formValues.searchFieldValues };

    if (!value || value?.length === 0) {
      delete updatedValues[key];
      setFormValues({ ...formValues, searchFieldValues: updatedValues });
      return;
    }

    updatedValues[key] = value;
    setFormValues({ ...formValues, searchFieldValues: updatedValues });
  };

  const handleSearchType = (searchTypeName) => {
    setFormValues({ ...formValues, searchFieldValues: {}, resetKey: uniqid() });
    setSearchType(searchTypeName);
  };

  const resetForm = () => (
    setFormValues({
      fileName: '',
      companyId: '',
      searchFieldValues: {},
      resetKey: uniqid(),
    })
  );

  /* eslint-disable camelcase */
  const handleUseCatalogChange = (useCatalog) => {
    setUploadMethod(useCatalog ? 'catalog' : 'csv');

    // Use functional update to avoid stale closure issues
    setFormValues((prevFormValues) => {
      const updatedValues = { ...prevFormValues.searchFieldValues };

      // Remove file fields when switching to catalog
      if (useCatalog && requiredFormInputs) {
        const fileFields = requiredFormInputs
          .filter((field) => MEDIA_FIELD_TYPES.includes(field.fieldType))
          .map((field) => field.key);

        fileFields.forEach((key) => {
          delete updatedValues[key];
        });
      }

      // Add or remove use_catalog flag
      if (useCatalog) {
        updatedValues.use_catalog = true;
      } else {
        delete updatedValues.use_catalog;
      }

      return { ...prevFormValues, searchFieldValues: updatedValues };
    });
  };

  const requiredKeys = Array.isArray(requiredFormInputs)
    ? requiredFormInputs.map(({ key }) => key)
    : [];

  // Filter out file/CSV fields from required validation when catalog is selected
  const filteredRequiredKeys = uploadMethod === 'catalog'
    ? requiredKeys.filter((key) => {
      const field = requiredFormInputs.find((f) => f.key === key);
      return !MEDIA_FIELD_TYPES.includes(field?.fieldType);
    })
    : requiredKeys;

  const submitEnabled = formValues.fileName?.length > 0
    && formValues.companyId?.length > 0
    && (filteredRequiredKeys.length === 0
    || filteredRequiredKeys.every((key) => key in formValues.searchFieldValues
      && (formValues.searchFieldValues[key].length > 0
      || formValues.searchFieldValues[key] !== null)));

  const handleFormSubmit = (e) => {
    e.preventDefault();

    submitImportRequest(
      {
        search: {
          file_name: formValues.fileName,
          company_id: Number(formValues.companyId),
          search_type_id: searchTypeId,
          search_field_values: formValues.searchFieldValues,
        },
      },
      () => resetForm(),
    );
  };
  /* eslint-enable camelcase */

  const pageTitle = searchTypeDisplay ? `Create Bulk ${searchTypeDisplay} Searches` : 'Create Bulk Searches';
  const submitMessage = `Creating bulk ${searchTypeDisplay} searches...`;

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: pageTitle,
        subTitle: 'File Imports',
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={page.highlight}
          message={submitMessage}
          contentWidth="20rem"
        />
      )}
      {importResult && (
        <SimpleModal
          noClickOutside
          small={small}
          modalTitle={importResult.success ? 'Import Successful' : 'Import Completed with Errors'}
          toggleModal={() => clearFileImportResult()}
          customHeaderBg={importResult.success ? 'var(--app-success)' : 'var(--app-warning)'}
          textColor={page.modalText}
          highlightColor={page.highlight}
          titleSize="1.5rem"
        >
          <ImportResultSummary
            importResult={importResult}
            textColor={page.modalText}
            clearImportResult={() => clearFileImportResult()}
          />
        </SimpleModal>
      )}
      <FormContents
        small={small}
        loading={loading || configLoading || !companyList}
        config={useConfig}
        searchTypeDisplay={searchTypeDisplay}
        requiredFormInputs={requiredFormInputs}
        optionalFormInputs={optionalFormInputs}
        formValues={formValues}
        handleSearchType={handleSearchType}
        handleFormValues={handleFormValues}
        setFormValues={setFormValues}
        minStartDate={today}
        timeZone={selectedTimeZone}
        setTimeZone={setSelectedTimeZone}
        searchTypeKey={searchTypeKey}
        resetForm={resetForm}
        submitEnabled={submitEnabled && !submitLoading}
        handleFormSubmit={handleFormSubmit}
        uploadMethod={uploadMethod}
        onUseCatalogChange={searchTypeKey?.includes('publisher') ? handleUseCatalogChange : null}
        companyList={companyList}
      />
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    formConfig: state.adminSearches.searchTypes,
    companyList: state.adminCompany.companies,
    companyFormFields: state.adminSearches.companyFormFields,
    importResult: state.adminFileImports.fileImportResult,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchCompanyList: fetchCompanyListRequest,
      clearCompanyList: clearCompanyListRequest,
      fetchFormConfig: fetchAdminSearchTypesRequest,
      clearFormConfig: clearAdminSearchTypesRequest,
      fetchCompanyFormFields: fetchCompanyFormFieldsRequest,
      clearCompanyFormFields: clearCompanyFormFieldsRequest,
      submitImportRequest: submitCreateBulkSearchesImportRequest,
      clearFileImportResult: clearFileImportResultRequest,
    },
    dispatch,
  );
}

ImportSearchesPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchCompanyList: PropTypes.func.isRequired,
  clearCompanyList: PropTypes.func.isRequired,
  fetchFormConfig: PropTypes.func.isRequired,
  clearFormConfig: PropTypes.func.isRequired,
  fetchCompanyFormFields: PropTypes.func.isRequired,
  clearCompanyFormFields: PropTypes.func.isRequired,
  submitImportRequest: PropTypes.func.isRequired,
  clearFileImportResult: PropTypes.func.isRequired,
  formConfig: PropTypes.shape({
    searchTypes: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        name: PropTypes.string.isRequired,
        id: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        fieldData: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string.isRequired,
            order: PropTypes.number.isRequired,
            config: PropTypes.objectOf(PropTypes.any),
            required: PropTypes.bool,
            fieldName: PropTypes.string.isRequired,
            fieldType: PropTypes.string.isRequired,
            selectable: PropTypes.bool,
            description: PropTypes.string,
          }).isRequired,
        ).isRequired,
      }),
    ),
  }),
  companyList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })),
  companyFormFields: PropTypes.objectOf(PropTypes.any),
  importResult: PropTypes.shape({
    success: PropTypes.bool.isRequired,
    fileImportId: PropTypes.string,
    fileImportName: PropTypes.string,
    summary: PropTypes.object,
    errors: PropTypes.arrayOf(PropTypes.any),
  }),
};

export default connect(mapStateToProps, mapDispatchToProps)(ImportSearchesPage);
